<template>
  <div class="todo-column p-3 mb-3">
    <div class="d-flex align-items-center justify-content-between mb-4">
      <h3 class="column-title m-0">
        <i class="fas fa-list-ul text-warning me-2"></i>To Do
      </h3>
      <span class="badge bg-warning text-dark rounded-pill shadow-sm">{{ ToDoTasks.length }}</span>
    </div>

    <div class="task-card mb-3 p-3 shadow-sm" v-for="(toDoTask, index) in ToDoTasks" :key="index">
      
      <div class="user-assignment d-flex align-items-center mb-3 pb-2 border-bottom border-light">
        <div class="avatar-wrapper">
          <div class="avatar-main shadow-sm bg-light">
            <img v-if="toDoTask.assigned_profile" 
                 :src="toDoTask.assigned_profile" 
                 class="avatar-img shadow-sm" 
                 alt="Assignee">
            <span v-else class="avatar-placeholder">
              {{ (toDoTask.assigned_name || 'U').charAt(0).toUpperCase() }}
            </span>
          </div>
          <div class="status-dot-pending"></div>
        </div>
        
        <div class="ms-3">
          <p class="user-name m-0">{{ toDoTask.assigned_name ||  'Personal' }}</p>
          <small class="user-role">Waiting to start</small>
        </div>
      </div>

      <div class="task-body">
        <h5 class="task-title text-dark">{{ toDoTask.title }}</h5>
        
        <div v-if="toDoTask.start_date" class="task-meta mt-2 d-inline-flex align-items-center">
          <i class="far fa-calendar-alt me-2 text-warning"></i>
          <span class="small font-weight-bold">{{ formatDate(toDoTask.start_date) }}</span>
        </div>
      </div>

      <div class="task-actions d-flex justify-content-end mt-3 pt-2 border-top">
        <button class="btn-action edit-btn" @click="$emit('edit-task', index, toDoTask.id)" title="Edit Task">
          <i class="fas fa-pen"></i>
        </button>
        <button class="btn-action detail-btn" @click="$emit('show-detail', toDoTask)" title="View Details">
            <i class="fas fa-comment"></i>
        </button>
        <button class="btn-action delete-btn" @click="$emit('delete-task', toDoTask.id, toDoTask.status)" title="Delete Task">
          <i class="fas fa-trash-alt"></i>
        </button>
      </div>
    </div>

    <div v-if="ToDoTasks.length === 0" class="empty-state text-center py-5">
      <i class="fas fa-clipboard-list fa-3x mb-3 text-muted opacity-25"></i>
      <p class="text-muted small">No pending tasks for now.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "ToDo",
  props: ["ToDoTasks"],
  methods: {
    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  }
}
</script>

<style scoped lang="scss">
// Colors & Shadows
$pending-color: #ffc107;
$card-bg: #ffffff;
$text-main: #2d3436;

.todo-column {
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  border: 1px dashed rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.column-title {
  font-weight: 800;
  color: $text-main;
  font-size: 1.2rem;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.task-card {
  background: $card-bg;
  border-radius: 16px;
  border: 1px solid rgba(0,0,0,0.03);
  position: relative;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  
  // Left border accent
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 20%;
    bottom: 20%;
    width: 4px;
    background-color: $pending-color;
    border-radius: 0 4px 4px 0;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
    border-color: rgba($pending-color, 0.4);
  }
}

.avatar-wrapper {
  position: relative;
  .avatar-main {
    width: 38px;
    height: 38px;
    border-radius: 10px; // Modern Squircle
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #f1f2f6;
  }
  .avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .avatar-placeholder {
    font-weight: 700;
    color: #636e72;
    font-size: 0.9rem;
  }
  .status-dot-pending {
    position: absolute;
    top: -3px;
    right: -3px;
    width: 10px;
    height: 10px;
    background-color: $pending-color;
    border: 2px solid #fff;
    border-radius: 50%;
  }
}

.user-name {
  font-weight: 700;
  font-size: 0.85rem;
  color: $text-main;
}

.user-role {
  font-size: 0.65rem;
  color: #b2bec3;
  text-transform: uppercase;
}

.task-title {
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.4;
  margin: 0;
}

.task-meta {
  background: rgba($pending-color, 0.1);
  padding: 2px 10px;
  border-radius: 6px;
  color: darken($pending-color, 20%);
}

.btn-action {
  background: transparent;
  border: none;
  padding: 6px 10px;
  margin-left: 5px;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s;

  &.edit-btn { color: #0984e3; &:hover { background: #e1f5fe; } }
  &.detail-btn { color: #00b894; &:hover { background: #e6faf5; } }
  &.delete-btn { color: #d63031; &:hover { background: #fff5f5; } }
}

.empty-state {
  border: 2px dashed #dfe6e9;
  border-radius: 15px;
}
</style>